import React, { useState } from 'react'
import {
    ChartsTooltipContainer,
    ScatterItemIdentifier,
    ScatterSeriesType,
    ScatterPlot as MuiScatterPlot,
    useItemTooltip,
    ChartsAxis,
    Toolbar,
    ToolbarButton,
    ChartsDataProvider,
    ChartsSurface,
    ChartsWrapper,
} from '@mui/x-charts'
import Paper from '@mui/material/Paper'
import { formatBigMoney, formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { AxisPropOption, Boundaries } from '../ScatterPlot'
import { formatValue } from './utils'
import { ScatterSelector } from './ScatterSelector'
import { ZoomOutIcon } from 'lucide-react'

export type ScatterSerie = Record<AxisPropOption, number | null> & { id: string; label: string; subEndDate: string }

export const FLScatterChart = ({
    onItemClick,
    onSelect,
    onZoom,
    selectedXAxisProperty,
    selectedYAxisProperty,
    series,
    seriesData,
    xLabel,
    yLabel,
}: {
    onItemClick: (item: ScatterSerie) => void
    onSelect: (
        pos: {
            minX: number
            maxX: number
            minY: number
            maxY: number
        } | null,
    ) => void
    onZoom: (b: Boundaries | null) => void
    selectedXAxisProperty: AxisPropOption
    selectedYAxisProperty: AxisPropOption
    series: ScatterSeriesType[]
    seriesData: ScatterSerie[]
    xLabel: string
    yLabel: string
}) => {
    const divRef = React.useRef<HTMLDivElement>(null)
    const { i18n } = useTranslation()
    const locale = i18n.language
    const [isZoomed, setIsZoomed] = useState(false)

    const handleZoom = (b: Boundaries | null) => {
        setIsZoomed(!!b)
        onZoom(b)
        onSelect(null)
    }

    const CustomTooltip = () => {
        const item2 = useItemTooltip<'scatter'>()

        if (!item2) return <ChartsTooltipContainer trigger='item' />

        const item = seriesData[item2?.identifier?.dataIndex]
        if (!item) return <ChartsTooltipContainer trigger='item' />

        const header = [
            item?.label || '?',
            item.offerSize ? formatBigMoney(item.offerSize, { locale }) : '',
            formatDateYYYYMMDD(item.subEndDate),
        ]
            .filter((v) => !!v)
            .join(' - ')

        return (
            <ChartsTooltipContainer trigger='item'>
                <Paper key={item.id} sx={{ paddingX: 4, paddingY: 2 }}>
                    <h4>{header}</h4>
                    <p>
                        {xLabel}: {formatValue(item, selectedXAxisProperty, { locale })}
                        <br />
                        {yLabel}: {formatValue(item, selectedYAxisProperty, { locale })}
                    </p>
                </Paper>
            </ChartsTooltipContainer>
        )
    }

    if (seriesData.length === 0) return null

    return (
        <div
            style={{
                aspectRatio: '1 / 1',
                paddingTop: '42px',
                marginTop: '-42px',
                width: '100%',
            }}
        >
            <ChartsDataProvider<'scatter'>
                dataset={seriesData}
                disableHitArea
                margin={{ right: 30 }}
                onItemClick={(e: Event, { dataIndex }: ScatterItemIdentifier) => {
                    const item = seriesData[dataIndex]

                    onItemClick(item)
                }}
                series={series}
                xAxis={[
                    {
                        label: xLabel,
                        valueFormatter: (val: number | null) => {
                            return formatValue({ [selectedXAxisProperty]: val }, selectedXAxisProperty, { locale })
                        },
                    },
                ]}
                yAxis={[
                    {
                        label: yLabel,
                        valueFormatter: (val: number | null) =>
                            formatValue({ [selectedYAxisProperty]: val }, selectedYAxisProperty, { locale }),
                        width: 80,
                    },
                ]}
            >
                <Toolbar>
                    <div style={{ marginRight: 'auto', paddingLeft: 10 }}>
                        Markera ett område för att filtrera tabellen.
                    </div>
                    {isZoomed && (
                        <ToolbarButton onClick={() => handleZoom(null)}>
                            <ZoomOutIcon />
                        </ToolbarButton>
                    )}
                </Toolbar>
                <ChartsWrapper ref={divRef}>
                    <ChartsSurface
                        sx={{
                            userSelect: 'none',
                        }}
                    >
                        <ChartsAxis />
                        <g data-drawing-container>
                            <MuiScatterPlot />
                        </g>
                        <ScatterSelector onChange={onSelect} onZoom={handleZoom} svgRef={divRef} />
                        <CustomTooltip />
                    </ChartsSurface>
                </ChartsWrapper>
            </ChartsDataProvider>
        </div>
    )
}
